import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Toast {
  id: number;
  message: string;
  tone: 'ok' | 'bad';
  action?: { label: string; onClick: () => void };
}

const Ctx = createContext<(t: Omit<Toast, 'id'>) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((xs) => [...xs, { ...t, id }]);
    setTimeout(() => setToasts((xs) => xs.filter((x) => x.id !== id)), 5000);
  }, []);
  return (
    <Ctx.Provider value={push}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex min-w-64 max-w-sm items-center gap-2.5 rounded-md border border-border bg-surface px-3.5 py-2.5 text-sm shadow-lg',
            )}
          >
            {t.tone === 'ok' ? <CheckCircle2 className="size-4 text-ok" /> : <XCircle className="size-4 text-bad" />}
            <span className="flex-1">{t.message}</span>
            {t.action && (
              <button className="font-medium text-brand-text hover:underline" onClick={t.action.onClick}>
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  const push = useContext(Ctx);
  return {
    ok: (message: string, action?: Toast['action']) => push({ message, tone: 'ok', action }),
    error: (message: string) => push({ message, tone: 'bad' }),
  };
}
